import React, { useState } from "react";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Client columns matching upload modal headers
export const CLIENT_COLUMNS = [
  { key: "companyName", header: "companyName", width: 30 },
  { key: "gstNumber", header: "gstNumber", width: 20 },
  { key: "state", header: "state", width: 18 },
  { key: "email", header: "email", width: 26 },
  { key: "address", header: "address", width: 42 },
  { key: "shippingAddress", header: "shippingAddress", width: 42 },
  { key: "vendorId", header: "vendorId", width: 16 },
  { key: "branch", header: "branch", width: 12 },
];

export const downloadSampleClientExcel = async ({
  fileName = "Sample_Client_Upload.xlsx",
} = {}) => {
  // Use current branch if logged in
  const currentBranch = Number(localStorage.getItem("branch")) || 1;

  // 3 sample rows so users know what to fill
  const sampleRows = [
    {
      companyName: "Tata Motors Ltd",
      gstNumber: "27AAACT2727Q1ZW",
      state: "Maharashtra",
      email: "contact@tatamotors.com",
      address: "Bombay House, 24 Homi Mody Street, Fort, Mumbai 400001",
      shippingAddress: "Pimpri Plant, Pune, Maharashtra 411018",
      vendorId: "VEND-101",
      branch: currentBranch,
    },
    {
      companyName: "Reliance Industries Ltd",
      gstNumber: "24AAACR5055K1ZV",
      state: "Gujarat",
      email: "info@ril.com",
      address: "Maker Chambers IV, Nariman Point, Mumbai 400021",
      shippingAddress: "Refinery Complex, Moti Khavdi, Jamnagar, Gujarat 361140",
      vendorId: "VEND-102",
      branch: currentBranch,
    },
    {
      companyName: "Larsen & Toubro Ltd",
      gstNumber: "27AAACL0149Q1ZL",
      state: "Maharashtra",
      email: "corporate@larsentoubro.com",
      address: "L&T House, Ballard Estate, Mumbai 400001",
      shippingAddress: "Powai Works, Saki Vihar Road, Mumbai 400072",
      vendorId: "VEND-103",
      branch: currentBranch,
    },
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Clients", {
    views: [{ showGridLines: true }],
  });

  worksheet.columns = CLIENT_COLUMNS.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.width,
    style: {
      protection: { locked: false },
    },
  }));

  // Blue header styling - locked from editing
  const headerRow = worksheet.getRow(1);
  headerRow.height = 28;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
    cell.font = {
      name: "Calibri",
      size: 12,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin", color: { argb: "FFB2B2B2" } },
      left: { style: "thin", color: { argb: "FFB2B2B2" } },
      bottom: { style: "thin", color: { argb: "FFB2B2B2" } },
      right: { style: "thin", color: { argb: "FFB2B2B2" } },
    };
    cell.protection = { locked: true };
  });

  // Add the 3 sample rows (all cells editable)
  sampleRows.forEach((sample) => {
    const row = worksheet.addRow(sample);
    row.height = 20;
    row.eachCell((cell, colNumber) => {
      cell.font = { name: "Calibri", size: 11 };
      cell.alignment = {
        vertical: "middle",
        horizontal: colNumber === 8 || colNumber === 2 ? "center" : "left",
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FFE0E0E0" } },
        left: { style: "thin", color: { argb: "FFE0E0E0" } },
        bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
        right: { style: "thin", color: { argb: "FFE0E0E0" } },
      };
      cell.protection = { locked: false };
    });
  });

  // Protect worksheet with password BFI - only header is locked, other fields are editable
  await worksheet.protect("BFI", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: true,
    formatColumns: true,
    formatRows: true,
    insertColumns: false,
    insertRows: true,
    insertHyperlinks: true,
    deleteColumns: false,
    deleteRows: true,
    sort: true,
    autoFilter: true,
    pivotTables: true,
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
};

// Download icon button with hover tooltip
export const SampleClientExcelDownload = ({
  tooltipTitle = "Sample Excel file of client",
  size = "small",
  color = "primary",
  sx = {},
}) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadSampleClientExcel();
      setTimeout(() => {
        setDownloading(false);
      }, 500);
    } catch (err) {
      console.error("Failed to download client sample excel:", err);
      toast.error(
        "Failed to download Sample Excel: " + (err.message || "Unknown error")
      );
      setDownloading(false);
    }
  };

  return (
    <Tooltip title={downloading ? "Downloading..." : tooltipTitle} arrow placement="top">
      <span>
        <IconButton
          id="clients-list-sample-excel-btn"
          size={size}
          color={color}
          disabled={downloading}
          onClick={handleDownload}
          sx={{
            ...sx,
          }}
        >
          {downloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <FileDownloadIcon fontSize={size === "small" ? "small" : "medium"} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SampleClientExcelDownload;
